/** Action items, "Waiting on you": what is waiting on the signed-in person (ADR-0212 decision 6c).
 *
 * **It reads the same thing the bell and Home read** (`useWaitingOnMe`): approvals that name the
 * person, tasks they own, bills to review. A row's open link is the item's own fix (`actionFor`, the
 * one place that turns a work item into a destination) except an approval, which goes to the record's
 * Workflow tab, where the decide panel is. Deciding is not done here: a second place to decide would
 * be a second place to forget a flag or a note, so the "Attest" button opens the receipt pane
 * (`AttestPane`) in a drawer and the decision is still the record page's own panel.
 *
 * **Empty and failed are different renderings.** "Nothing is waiting on you." is a claim that a
 * person's queue is clear; a dropped connection must not be able to say it. The hook never defaults,
 * a failed read is the shared `ReadFailed` with a retry in the table's place, and a role that is never
 * asked for anything has a dash for the count, not a zero.
 *
 * **Server counts only.** The Waiting tile is the server's sum; Overdue counts the rows held and
 * carries a `+` when the page is not the whole list; the rows are never re-sorted into a different
 * order than the one asked for (a click on a header orders the page held).
 */

import { CheckCircle, ListChecks } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { mayHaveQueue, useAwaitingMe } from "../api/approvals";
import type { AwaitingRequest } from "../api/approvals";
import { useUnreadCount } from "../api/notifications";
import { useReader } from "../api/profile";
import { useWaitingOnMe, waitingText } from "../api/waiting";
import type { WaitingItem } from "../api/waiting";
import { ReadFailed } from "../components/Loaded";
import { Drawer, EmptyState, FilterLine, GridTable, HowDrawer, StatusChip, TablePage, Tile, TileStrip } from "../components/ui";
import { EMPTY_FILTERS } from "../components/ui";
import type { FilterState, GridColumn } from "../components/ui";
import { instantDate } from "../lib/format";
import { AttestPane } from "./ApprovalsAttest";
import { DatesReminder } from "./ApprovalsDates";
import { useGridSort } from "./gridSort";
import { keepSearch } from "./search";
import { TYPE_LABEL, destination, dueText, rowKind, typeOf } from "./waitingRows";
import type { Screen } from "./useScreen";

/** Stage 2 of 3 - Controller sign-off. The number is the server's `current_position`; nothing here
 * assigns one. */
function stageSentence(request: AwaitingRequest): string {
  const of = request.stages.length;
  const at = request.current_position;
  return at === null ? request.stage_name : `Stage ${String(at)} of ${String(of)} · ${request.stage_name}`;
}

/** Its own component so the reader's preferences are read only when a request has a date to place. */
function StageLine({ request }: { request: AwaitingRequest }) {
  const reader = useReader();
  return (
    <small data-testid="approval-stage">
      {stageSentence(request)} · submitted {instantDate(request.submitted_at, reader)}
    </small>
  );
}

export function WaitingPage({ screen, role }: { screen: Screen; role: string | undefined }) {
  const waiting = useWaitingOnMe();
  const unread = useUnreadCount();
  const queue = useAwaitingMe(mayHaveQueue(role));
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTERS);
  const [attesting, setAttesting] = useState<AwaitingRequest | null>(null);

  const all = waiting.data?.items ?? [];
  const kinds = [...new Set(all.map((item) => item.kind))];
  const picked = filter.values.type ?? [];
  const needle = filter.search.trim().toLowerCase();
  const rows = all.filter((item) => {
    if (picked.length > 0 && !picked.includes(item.kind)) return false;
    return !needle || `${item.title} ${typeOf(item)} ${item.facility_name ?? ""}`.toLowerCase().includes(needle);
  });
  const { sorted, sort, setSort } = useGridSort(rows, {
    item: (r) => r.title,
    type: (r) => typeOf(r),
    site: (r) => r.facility_name,
    due: (r) => r.due,
  });

  const requestOf = (item: WaitingItem) =>
    item.kind === "approval_waiting"
      ? queue.data?.find((request) => `approval_waiting:${request.id}` === item.item_key)
      : undefined;

  const columns: GridColumn<WaitingItem>[] = [
    {
      id: "item",
      header: "Item",
      sortable: true,
      cell: (item) => {
        const to = destination(item);
        const request = requestOf(item);
        return (
          <span data-testid="approval-row" data-kind={rowKind(item)} data-item-key={item.item_key}>
            <Link
              to={to.to}
              search={{ ...keepSearch(screen), ...(to.search ?? {}) }}
              data-testid="approval-review-link"
            >
              {item.title}
            </Link>
            {request ? (
              <>
                <br />
                <StageLine request={request} />
                {request.blocked_by.length > 0 ? (
                  <>
                    {" "}
                    <StatusChip tone="warn" size="sm">
                      Blocked
                    </StatusChip>
                    <br />
                    <small data-testid="approval-blocked">
                      Approving will be refused until {request.blocked_by.length === 1 ? "this flag is" : "these flags are"}{" "}
                      resolved: {request.blocked_by.join("; ")}
                    </small>
                  </>
                ) : null}
              </>
            ) : null}
          </span>
        );
      },
    },
    { id: "type", header: "Type", sortable: true, cell: (item) => typeOf(item) },
    { id: "site", header: "Site", sortable: true, cell: (item) => item.facility_name ?? "" },
    {
      id: "due",
      header: "Due",
      sortable: true,
      cell: (item) => (
        <>
          {dueText(item)}
          {item.overdue ? (
            <>
              {dueText(item) ? " " : ""}
              <StatusChip tone="warn" size="sm">
                Overdue
              </StatusChip>
            </>
          ) : null}
        </>
      ),
    },
    {
      id: "act",
      header: "Attest",
      cell: (item) => {
        const request = requestOf(item);
        return request ? (
          <button
            type="button"
            data-testid="approval-attest"
            aria-label={`Attest the request waiting at ${request.stage_name}`}
            onClick={() => setAttesting(request)}
          >
            Attest
          </button>
        ) : null;
      },
    },
  ];

  // An Attest column nobody can use is an empty column on the page (the walk's Approver saw one over a
  // single filing to send): it is drawn only while a row holds an approval request to attest.
  const shownColumns = rows.some((item) => requestOf(item) !== undefined)
    ? columns
    : columns.filter((column) => column.id !== "act");

  const data = waiting.data;
  const overdue = data ? `${String(data.overdue)}${data.truncated ? "+" : ""}` : "-";
  const state = waiting.error ? (
    <ReadFailed of="What is waiting on you" error={waiting.error} onRetry={() => void waiting.refetch()} />
  ) : undefined;

  return (
    <>
      <TablePage
        icon={ListChecks}
        title="Action items"
        actions={
          <span>
            <HowDrawer infoKey="actions.how-decisions" label="How a decision is made" />
            <DatesReminder />
            {role !== undefined && !mayHaveQueue(role) ? (
              <span data-testid="approvals-no-queue">
                <StatusChip tone="neutral" size="sm">
                  Only an Admin or an Approver decides approvals
                </StatusChip>
              </span>
            ) : null}
          </span>
        }
        label="Waiting on you"
        tiles={
          // The unread count is a read of its own, and the one the table's failure does not cover: a
          // dash beside a table that loaded read as "no unread notifications" (G-P9-10).
          <TileStrip
            label="Waiting on you at a glance"
            reads={[{ of: "The count of unread notifications", read: unread }]}
          >
            <Tile
              value={waitingText(role, data?.total)}
              label="Waiting on you"
              infoKey="actions.waiting"
              testId="tile-waiting"
              read={waiting}
            />
            <Tile value={overdue} label="Overdue" testId="tile-overdue" read={waiting} />
            <Tile
              value={unread.data !== undefined ? String(unread.data) : "-"}
              label="Unread notifications"
              read={unread}
            />
          </TileStrip>
        }
        filters={
          // Nothing waits and nothing is filtered away: one line saying so, not a search over nothing.
          data && data.total === 0 && filter.search.trim() === "" ? undefined : (
            <FilterLine
              filters={[
                {
                  id: "type",
                  label: "Type",
                  options: kinds.map((kind) => ({ value: kind, label: TYPE_LABEL[kind] ?? kind })),
                },
              ]}
              state={filter}
              onChange={setFilter}
              resultCount={data ? { shown: rows.length, total: data.total, noun: "items" } : undefined}
            />
          )
        }
        table={
          waiting.error ? undefined : (
            <>
              {mayHaveQueue(role) && queue.error ? (
                <ReadFailed
                  of="Your approval queue"
                  error={queue.error}
                  onRetry={() => void queue.refetch()}
                />
              ) : null}
              <GridTable
                caption="What is waiting on you"
                columns={shownColumns}
                rows={sorted}
                rowKey={(item) => item.item_key}
                rowLabel={(item) => item.title}
                loading={waiting.isPending}
                sort={sort}
                onSortChange={setSort}
                onRowOpen={(item) => {
                  const to = destination(item);
                  void navigate({ to: to.to, search: { ...keepSearch(screen), ...(to.search ?? {}) } });
                }}
                empty={<EmptyState icon={CheckCircle} title="Nothing needs you" compact testId="approvals-empty" />}
                testId="approvals-queue"
              />
            </>
          )
        }
        state={state}
      />
      <Drawer
        open={attesting !== null}
        onClose={() => setAttesting(null)}
        title="Attest"
        width="md"
      >
        {attesting ? (
          <AttestPane
            request={attesting}
            method={screen.method ?? "location"}
            onOpenReceipt={(id) => {
              setAttesting(null);
              screen.openReceipt(id);
            }}
            onClose={() => setAttesting(null)}
          />
        ) : null}
      </Drawer>
    </>
  );
}
